import i18n from './index';
import { enumLabel } from './enumLabel';

/**
 * Title and message of a server notification in the reader's language (medopd-docs 106).
 *
 * The server stores the English text it always did, plus — for senders that have been converted — a
 * `messageKey` and the `params` the English was built from. Render the translated sentence when the key
 * exists in the `notifications` namespace; otherwise fall back to the stored English, so an older
 * notification or a sender without a key still reads correctly rather than as a raw key.
 *
 * Params are names, numbers, test names and codes. Codes the server marks with a `…Code` suffix are
 * shown through their enum label (e.g. `statusCode: 'UNDER_REVIEW'` → `status: 'Under review'`).
 */
export interface LocalizableNotification {
  title?: string | null;
  message?: string | null;
  messageKey?: string | null;
  params?: Record<string, string | number> | null;
}

export function notificationText(n: LocalizableNotification): { title: string; message: string } {
  const title = n.title ?? '';
  const message = n.message ?? '';
  const key = n.messageKey;
  if (!key) return { title, message };
  const values: Record<string, string | number> = { ...(n.params ?? {}) };
  // `<name>Code` + `<name>Group` pairs become a translated `<name>` value.
  for (const [k, v] of Object.entries(n.params ?? {})) {
    const m = k.match(/^(\w+)Code$/);
    const group = m ? n.params?.[`${m[1]}Group`] : undefined;
    if (m && group) values[m[1]] = enumLabel(String(group), String(v));
  }
  return {
    title: i18n.t(`notifications:${key}Title`, { ...values, defaultValue: title }),
    message: i18n.t(`notifications:${key}Message`, { ...values, defaultValue: message }),
  };
}
